'use client';

import { useRovingRadioGroup } from '@/hooks/useRovingRadioGroup';
import type { LaunchPromoConfig } from '@/lib/payments/pricing-fetcher';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

export type PricingMode = 'monthly' | 'annual' | 'pack';

interface PricingModeToggleProps {
  mode: PricingMode;
  onChange: (mode: PricingMode) => void;
  savingsPct: number | null;
  /** Backend-driven launch promo. When active, the launch-promo pill
   *  follows the active segment and supersedes the static
   *  ``Ušetríte X %`` annual savings badge. */
  launchPromo: LaunchPromoConfig;
  /**
   * Whether to render the one-time Pack segment. Packs are audience-agnostic
   * AI top-ups that only make sense for the Individuals audience, so the
   * Lawyers tab passes ``false`` to show just Monthly | Annual. Defaults to
   * ``true`` (the Individuals tab + every legacy call site).
   */
  showPack?: boolean;
}

/**
 * Unified pricing-mode selector (Monthly | Annual | Pack).
 *
 * Elevates one-time pack purchases to a first-class peer of subscription
 * cadences so both show in the same above-the-fold slot. The savings badge
 * stays anchored to the Annual segment.
 */
export function PricingModeToggle({
  mode,
  onChange,
  savingsPct,
  launchPromo,
  showPack = true,
}: PricingModeToggleProps) {
  const t = useTranslations('PricingPage');

  // Pick the active-segment discount % from the backend payload.
  // ``mode === 'pack'`` is not part of the subscription promo — pill hides.
  //
  // Filter out ``prof_profile``: it's a paid *add-on* (Profil+), not a
  // monthly/annual subscription tier. Its discount (currently -80 %) belongs
  // on its own card, not on the global tier-cadence toggle — otherwise the
  // Monthly pill would advertise -80 % and contradict the actual tier deal
  // (-40 % monthly / -60 % annual on Starter + Pro).
  const tierDiscounts = launchPromo.active
    ? Object.entries(launchPromo.plans)
        .filter(([tag]) => tag !== 'prof_profile')
        .map(([, d]) => d)
    : [];
  const segmentPct =
    mode === 'monthly'
      ? Math.max(0, ...tierDiscounts.map((d) => d.month_pct ?? 0))
      : mode === 'annual'
        ? Math.max(0, ...tierDiscounts.map((d) => d.year_pct ?? 0))
        : 0;
  const showLaunchPromoPill = launchPromo.active && segmentPct > 0;

  // Static annual-vs-monthly savings badge — only meaningful when the
  // user is comparing subscription cadences (monthly vs annual). Hide on
  // Pack mode since one-time packs have no cadence comparison, and
  // suppress while the launch-promo pill is active to avoid double-stack.
  const hasSavings =
    !showLaunchPromoPill &&
    mode !== 'pack' &&
    savingsPct != null &&
    savingsPct > 0;

  // Inline per-segment discount chips (persistent on both active AND inactive
  // segments) so a Monthly-leaning user still sees that Annual is the better
  // deal — the SOTA pattern Notion / Linear / Stripe converged on for launch
  // promos. The big floating pill above the active segment stays; these
  // chips do the *comparison* job, the floating pill does the *emphasis* job.
  const monthlyPct = Math.max(0, ...tierDiscounts.map((d) => d.month_pct ?? 0));
  const annualPct = Math.max(0, ...tierDiscounts.map((d) => d.year_pct ?? 0));

  const segments: {
    key: PricingMode;
    labelKey: string;
    chipPct: number;
  }[] = [
    { key: 'monthly', labelKey: 'billingMonthly', chipPct: monthlyPct },
    { key: 'annual', labelKey: 'billingAnnual', chipPct: annualPct },
    ...(showPack
      ? [{ key: 'pack' as const, labelKey: 'billingPack', chipPct: 0 }]
      : []),
  ];

  // CSS-driven sliding indicator: segments are equal-width grid columns, so
  // the indicator's geometry is a pure function of the active index — no DOM
  // measurement, no useLayoutEffect. The SSR markup is already correct, which
  // kills the zero-size flash on first paint (#5014). The `transition` on
  // ``transform``/``left`` still animates smoothly between segments.
  const segmentCount = segments.length;
  const activeIndex = Math.max(
    0,
    segments.findIndex((s) => s.key === mode)
  );
  const segmentWidthPct = 100 / segmentCount;
  // Anchor the launch-promo pill to the active segment's horizontal center,
  // expressed as a fraction of the inner track so it stays in lock-step with
  // the indicator under any reflow (font-load, viewport resize).
  const promoBadgeStyle: React.CSSProperties = {
    left: `${(activeIndex + 0.5) * segmentWidthPct}%`,
  };

  // Roving-tabindex keyboard nav (ARIA APG radio-group pattern, #5013): the
  // segmented control is a single Tab stop and Arrow / Home / End move both
  // selection and focus. Refs let focus follow the freshly selected segment.
  const segmentRefs = useRef<Partial<Record<PricingMode, HTMLButtonElement>>>(
    {}
  );
  const radio = useRovingRadioGroup<PricingMode>({
    keys: segments.map((s) => s.key),
    selected: mode,
    onSelect: onChange,
    refs: segmentRefs,
  });

  return (
    <div className='flex flex-col items-center justify-center mb-8'>
      <div
        role='radiogroup'
        aria-label={t('pricingModeAriaLabel')}
        className='relative inline-flex rounded-full border border-line-default bg-surface-card p-1'
      >
        {/* Inner track — zero-padding content box that hosts both the
            absolutely-positioned sliding indicator and the equal-width
            segment grid. Percentages on the indicator/pill resolve against
            this box, so geometry is exact AND measurement-free. */}
        <div className='relative grid grid-flow-col auto-cols-fr items-center'>
          {/* Sliding indicator — sized/positioned from the active index in
              CSS, so it renders correctly on the SSR first paint (no
              measurement, no zero-size flash #5014). */}
          <div
            className='absolute top-0 left-0 h-full rounded-full [background:var(--gradient-button)] shadow-sm transition-transform duration-300 ease-out'
            style={{
              width: `${segmentWidthPct}%`,
              transform: `translateX(${activeIndex * 100}%)`,
            }}
            aria-hidden='true'
          />

          {segments.map(({ key, labelKey, chipPct }) => {
            const { tabIndex, onKeyDown } = radio(key);
            return (
              <button
                key={key}
                ref={(el) => {
                  if (el) segmentRefs.current[key] = el;
                  else delete segmentRefs.current[key];
                }}
                type='button'
                role='radio'
                aria-checked={mode === key}
                tabIndex={tabIndex}
                onClick={() => onChange(key)}
                onKeyDown={onKeyDown}
                className={cn(
                  'relative z-10 px-4 sm:px-5 py-2 text-sm font-medium rounded-full transition-colors duration-200 min-h-[44px] whitespace-nowrap inline-flex items-center justify-center gap-2',
                  mode === key
                    ? 'text-cta-text'
                    : 'text-content-secondary hover:text-content-primary'
                )}
              >
                {t(labelKey as Parameters<typeof t>[0])}
                {launchPromo.active && chipPct > 0 && mode !== key && (
                  <span className='rounded-full px-1.5 py-0.5 text-xs font-bold leading-none bg-status-success/15 text-status-success-text'>
                    −{chipPct} %
                  </span>
                )}
              </button>
            );
          })}

          {/* Savings badge — centered over the toggle (visually sits above
              the Annual segment, which is the middle of three). Static
              annual-vs-monthly only; suppressed while the launch-promo
              pill is active. */}
          {hasSavings && (
            <span
              className={cn(
                'absolute -top-4 left-1/2 -translate-x-1/2',
                'px-3 py-1 rounded-full',
                'bg-status-success-text text-surface-card',
                'text-sm font-bold whitespace-nowrap',
                'shadow-lg shadow-status-success/25',
                '-rotate-6',
                'pointer-events-none',
                'animate-in fade-in zoom-in-75 duration-500'
              )}
            >
              {t('save', { pct: savingsPct })}
            </span>
          )}

          {/* Launch-promo pill — anchored to the active segment center via the
              same index-driven CSS the sliding indicator uses. Text is
              context-aware: monthly → "−40 % · prvých 12 mesiacov",
              annual → "−60 % · prvý rok". */}
          {showLaunchPromoPill && (
            <span
              className={cn(
                'absolute -top-4 -translate-x-1/2',
                'px-3 py-1 rounded-full',
                'bg-status-success-text text-surface-card',
                'text-sm font-bold whitespace-nowrap',
                'shadow-lg shadow-status-success/25',
                '-rotate-6',
                'pointer-events-none',
                'transition-all duration-300 ease-out'
              )}
              style={promoBadgeStyle}
            >
              {t(
                mode === 'annual' ? 'promoRibbonAnnual' : 'promoRibbonMonthly',
                {
                  pct: segmentPct,
                }
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
